'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { AvatarPicker } from '@/components/images/avatar-picker';
import { UserAvatar, type UserAvatarSize } from '@/components/images/user-avatar';
import { Icon } from '@/components/ui/icon';
import { Edit02Icon } from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';
interface EditableAvatarProps {
  /**
   * Owning user's id. Required by the picker — drives both the live
   * preview palette (`pickBrandBluePalette`) and the per-variant base
   * seed for the generative flow.
   */
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  /**
   * NSFW flag forwarded from the SDK `avatarNsfw` field. When `true`
   * the underlying `<UserAvatar>` renders the blurred-overlay state
   * until the viewer taps to reveal. The owner sees their own avatar
   * blurred too — pair this surface with the
   * `image.nsfw.owner_avatar_notice` copy so they understand why.
   */
  nsfw?: boolean | null;
  /**
   * Owning user's `avatarPreferences.rasterise` — drives both the
   * displayed `<UserAvatar>` rendering AND the initial state of the
   * "Dithered / Original" toggle inside the picker.
   */
  rasterise: boolean;
  size?: UserAvatarSize;
  /** Called with the chosen avatar URL (or `''` when cleared) once the picker confirms. */
  onAvatarChange: (url: string, avatarNsfw?: boolean | null) => void;
  /** Applied to the outer trigger button — use for positioning (e.g. `absolute -bottom-10`). */
  className?: string;
  /** Applied to the inner `<UserAvatar>` — use for the ring halo when the avatar overlaps a banner. */
  avatarClassName?: string;
}

/**
 * `<EditableAvatar>` — single source of truth for the "click to edit profile photo"
 * trigger. Wraps `<UserAvatar>` with a hover/focus-visible edit-icon overlay and
 * mounts the shared `<AvatarPicker>` modal. Used by both the profile-settings page
 * and the onboarding profile stage.
 */
export function EditableAvatar(t0) {
  const $ = _c(31);
  const {
    userId,
    displayName,
    avatarUrl,
    nsfw: t1,
    rasterise,
    size: t2,
    onAvatarChange,
    className,
    avatarClassName
  } = t0;
  const nsfw = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "2xl" : t2;
  const t = useTranslations("profile");
  const [open, setOpen] = useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setOpen(true);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== className) {
    t4 = cn("group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary focus-visible:ring-offset-2", className);
    $[1] = className;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t) {
    t5 = t("avatar.title");
    $[3] = t;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = nsfw ?? false;
  let t7;
  if ($[5] !== avatarClassName) {
    t7 = avatarClassName !== undefined ? {
      className: avatarClassName
    } : {};
    $[5] = avatarClassName;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== avatarUrl || $[8] !== displayName || $[9] !== rasterise || $[10] !== size || $[11] !== t6 || $[12] !== t7 || $[13] !== userId) {
    t8 = <UserAvatar size={size} userId={userId} displayName={displayName} avatarUrl={avatarUrl} nsfw={t6} rasterise={rasterise} {...t7} />;
    $[7] = avatarUrl;
    $[8] = displayName;
    $[9] = rasterise;
    $[10] = size;
    $[11] = t6;
    $[12] = t7;
    $[13] = userId;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-surface-scrim-default text-white opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none"><Icon icon={Edit02Icon} className="size-5" aria-hidden="true" /></span>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t8) {
    t10 = <button type="button" onClick={t3} className={t4} aria-label={t5}>{t8}{t9}</button>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = displayName ?? "";
  let t12;
  if ($[20] !== avatarUrl || $[21] !== nsfw || $[22] !== onAvatarChange || $[23] !== open || $[24] !== rasterise || $[25] !== t11 || $[26] !== userId) {
    t12 = <AvatarPicker open={open} onOpenChange={setOpen} userId={userId} currentAvatarUrl={avatarUrl} currentAvatarNsfw={nsfw} currentRasterise={rasterise} searchSeedQuery={t11} onAvatarChange={onAvatarChange} />;
    $[20] = avatarUrl;
    $[21] = nsfw;
    $[22] = onAvatarChange;
    $[23] = open;
    $[24] = rasterise;
    $[25] = t11;
    $[26] = userId;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t10 || $[29] !== t12) {
    t13 = <>{t10}{t12}</>;
    $[28] = t10;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  return t13;
}